import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from "@store/hooks";
import { setBaseView, setPaperEditorMode } from "@store/slices/viewSlice";

/**
 * useKeyboardShortcuts - Hook that enables keyboard shortcuts for view switching
 *
 * Shortcuts:
 * - Ctrl+1 (Cmd+1 on Mac): Switch to Reading mode
 * - Ctrl+2 (Cmd+2 on Mac): Switch to Canvas mode
 * - Ctrl+3 (Cmd+3 on Mac): Toggle Paper Editor (cycles: hidden -> modal -> fullscreen -> hidden)
 * - Escape: Exit fullscreen paper editor (if in fullscreen mode)
 */
export function useKeyboardShortcuts() {
  const dispatch = useAppDispatch();
  const { baseView, paperEditorMode } = useAppSelector((state) => state.view);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore if user is typing in input/textarea or contenteditable
      const target = event.target as HTMLElement;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target.isContentEditable
      ) {
        return;
      }

      // Ctrl/Cmd + 1: Switch to Reading mode
      if ((event.ctrlKey || event.metaKey) && event.key === '1') {
        event.preventDefault();
        dispatch(setBaseView('reading'));
        console.log('Keyboard shortcut: Switched to Reading mode');
      }

      // Ctrl/Cmd + 2: Switch to Canvas mode
      if ((event.ctrlKey || event.metaKey) && event.key === '2') {
        event.preventDefault();
        dispatch(setBaseView('canvas'));
        console.log('Keyboard shortcut: Switched to Canvas mode');
      }

      // Ctrl/Cmd + 3: Toggle Paper Editor (cycles through modes)
      if ((event.ctrlKey || event.metaKey) && event.key === '3') {
        event.preventDefault();

        // Cycle: hidden -> modal -> fullscreen -> hidden
        if (paperEditorMode === 'hidden') {
          dispatch(setPaperEditorMode('modal'));
          console.log('Keyboard shortcut: Paper editor opened in modal mode');
        } else if (paperEditorMode === 'modal') {
          dispatch(setPaperEditorMode('fullscreen'));
          console.log('Keyboard shortcut: Paper editor switched to fullscreen');
        } else {
          dispatch(setPaperEditorMode('hidden'));
          console.log('Keyboard shortcut: Paper editor hidden');
        }
      }

      // Escape: Exit fullscreen paper editor
      if (event.key === 'Escape' && paperEditorMode === 'fullscreen') {
        event.preventDefault();
        dispatch(setPaperEditorMode('modal'));
        console.log('Keyboard shortcut: Exited paper editor fullscreen mode');
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [dispatch, baseView, paperEditorMode]);
}
